/**
 * Per-run store: materialized state + a transcript window, never a replay from event 0.
 *
 *   sealed local cache ─┐
 *   daemon snapshot ────┴─▶ RunMaterial (exact run state at seq S) ──reduceMaterial(seq > S)──▶
 *   events from a window cut ──reduceTranscript──▶ newest turns ──composeWindow──▶ TranscriptView
 *
 * On open the store seeds its material (the newer of the sealed local cache and the daemon's
 * snapshot), picks a window cut a few turns back, and subscribes from there: the header, verdict
 * and todos are exact immediately, and only the newest turns' events are fetched and decrypted.
 * Earlier turns load on demand (`loadOlder`). A run with no snapshot yet (older runs) replays
 * once and caches the resulting material, so the next load is snapshot + tail as well.
 *
 * Pure and framework-free (the React hook in session.ts wraps it with a rAF batcher and
 * useSyncExternalStore). Handles the viewer's connection state and inserts a client-side
 * `reconnected` marker at the point a resumed stream picks up again.
 */
import type { TranscriptEvent } from '@viko/protocol'
import {
  buildWindow,
  type Connection,
  composeWindow,
  createTranscript,
  emptyMaterial,
  type OlderTurns,
  type RunMaterial,
  reduceMaterial,
  reduceTranscript,
  type TranscriptInput,
  type TranscriptView,
  type WindowCut,
  windowSafe,
} from '@viko/transcript-ui'
import type { BatchMeta, ConnState } from './hub.js'
import type { OpenedSnapshot } from './run-snapshots.js'

export function toViewerConnection(s: ConnState): Connection {
  if (s === 'live') return 'live'
  if (s === 'offline') return 'offline'
  // idle/connecting before the first welcome still shows history, marked as reconnecting.
  return 'reconnecting'
}

/** While history pages are still arriving, publish at most this often (one render per window). */
export const REPLAY_PUBLISH_MS = 250
/** Turns a session opens with; older ones load on demand. */
export const WINDOW_TURNS = 4
/** Turns each "load earlier" adds. */
export const OLDER_TURNS = 8
/** How long opening waits for the hub's snapshot before replaying without one. */
export const SNAPSHOT_WAIT_MS = 2500
const CACHE_SAVE_MS = 1500

export interface RunStoreDeps {
  /** The sealed local material (instant on reload). */
  cache?: {
    load(): Promise<{ seq: number; epoch: number; material: RunMaterial } | null>
    save(material: RunMaterial): Promise<void>
  }
  /** The daemon's newest snapshot (authenticated; batched with other runs). */
  snapshot?: () => Promise<OpenedSnapshot | undefined>
  /** One authenticated page of events after `after` (older turns). */
  readPage?: (after: number) => Promise<{ events: TranscriptEvent[]; more: boolean }>
  now?: () => number
}

export class RunStore {
  private locked = false
  /** The run's materialized state (exact at `material.header.lastSeq`). */
  material: RunMaterial | null = null
  /** Reduced window (turns from `windowStart` on). */
  private win: TranscriptView
  /** The window's events, seq ascending (rebuilt into `win` when older turns are prepended). */
  private events: TranscriptEvent[] = []
  /** First seq of the window (1 = the full transcript). */
  windowStart = 1
  private cut: WindowCut | undefined
  private view: TranscriptView
  /** What subscribers see; lags `view` only while replay pages are still arriving. */
  private published: TranscriptView
  private listeners = new Set<() => void>()
  private reconnects = 0
  private lastPublish = 0
  private replayTimer: ReturnType<typeof setTimeout> | undefined
  private saveTimer: ReturnType<typeof setTimeout> | undefined
  private savedSeq = 0
  private seeding: Promise<void> | undefined
  private olderState: { loading: boolean; error: boolean } = { loading: false, error: false }
  private olderRun: Promise<void> | undefined
  /** A replay page said more history follows. */
  replaying = false
  /** The history replay finished (last page) or a live batch arrived since the store opened. */
  caughtUp = false
  /** Where the seeded state came from (diagnostics and the load-time metric). */
  seededFrom: 'cache' | 'snapshot' | 'replay' | undefined

  constructor(
    readonly runId: string,
    private readonly deps: RunStoreDeps = {},
  ) {
    // Empty runId: the reducer adopts runId and agent (claude/codex) from the first event.
    this.win = createTranscript()
    this.view = this.win
    this.published = this.view
  }

  get snapshot(): TranscriptView {
    return this.published
  }

  subscribe = (cb: () => void) => {
    this.listeners.add(cb)
    return () => this.listeners.delete(cb)
  }

  /** Where a subscription must start so the window and the material both stay contiguous. */
  cursor(): number {
    return Math.floor(Math.max(this.win.lastSeq, this.windowStart - 1))
  }

  /** Earlier turns not loaded (for the viewer's "load earlier" control). */
  older(): OlderTurns | undefined {
    const turns = this.win.turnBase ?? 0
    if (turns <= 0 || !this.deps.readPage) return undefined
    return {
      turns,
      loading: this.olderState.loading,
      error: this.olderState.error,
      onLoad: () => void this.loadOlder(),
    }
  }

  /**
   * Seed once: the local cache renders at once; the hub snapshot replaces it when newer. Then the
   * window is chosen. Resolves before the store should subscribe.
   */
  ready(): Promise<void> {
    this.seeding ??= this.seedOnce()
    return this.seeding
  }

  private async seedOnce() {
    const { cache, snapshot } = this.deps
    const fromHub = snapshot
      ? withTimeout(
          snapshot().catch(() => undefined),
          SNAPSHOT_WAIT_MS,
        )
      : Promise.resolve(undefined)
    const local = await (cache?.load().catch(() => null) ?? Promise.resolve(null))
    if (local) {
      // Exact at its seq: render and stream the tail now; a newer daemon snapshot, when it
      // arrives, only moves the material ahead (events it covers are then skipped).
      this.seed(local.material, 'cache')
      void fromHub.then((hub) => hub && this.seed(hub.material, 'snapshot'))
    } else {
      const hub = await fromHub
      if (hub) this.seed(hub.material, 'snapshot')
    }
    if (this.locked) return
    if (!this.material) {
      // No state anywhere (older run, new run): replay once from the start and cache the result.
      // Empty: runId and agent come from the first event (as in the transcript).
      this.material = emptyMaterial()
      this.seededFrom = 'replay'
    }
    this.chooseWindow()
    this.publishNow()
  }

  /**
   * Adopt a newer authenticated material (seeding, or a daemon push while the store is not
   * streaming). Events after it keep applying on top; nothing older ever replaces it.
   */
  seed(material: RunMaterial, from: 'cache' | 'snapshot' = 'snapshot') {
    if (this.locked) return false
    if (material.header.runId && this.runId && material.header.runId !== this.runId) return false
    if (this.material && material.header.lastSeq <= this.material.header.lastSeq) return false
    this.material = material
    this.seededFrom ??= from
    if (from === 'cache') this.savedSeq = Math.max(this.savedSeq, material.header.lastSeq)
    this.publishNow()
    return true
  }

  private chooseWindow() {
    const m = this.material
    if (!m || this.events.length) return
    const cut = pickWindowCut(m, WINDOW_TURNS)
    this.cut = cut
    this.windowStart = cut ? cut[0] : 1
    this.win = { ...createTranscript(), turnBase: cut ? cut[1] - 1 : 0 }
  }

  /** Apply a batch of hub events (already deduped by cursor). */
  applyEvents(batches: { events: TranscriptEvent[]; meta: BatchMeta }[]) {
    if (this.locked) return
    let w = this.win
    let m = this.material
    for (const { events, meta } of batches) {
      const first = events[0]
      if (meta.resumed && first && w.lastSeq > 0) {
        this.reconnects++
        w = reduceTranscript(w, this.reconnectMarker(first, w.lastSeq))
      }
      for (const e of events) {
        if (m && e.seq > m.header.lastSeq) m = reduceMaterial(m, e)
        if (e.seq >= this.windowStart && e.seq > w.lastSeq) {
          w = reduceTranscript(w, e as TranscriptInput)
          this.events.push(e)
        }
      }
    }
    this.win = w
    this.material = m
    const last = batches.at(-1)?.meta
    if (last) {
      this.replaying = last.replay && last.more === true
      if (!this.replaying) this.caughtUp = true
    }
    // A window that turns out to reference an earlier turn widens until it is exact.
    if (
      this.cut &&
      m &&
      !this.replaying &&
      this.events[0]?.seq === this.windowStart &&
      !windowSafe(this.events, m, this.cut)
    )
      void this.loadOlder()
    this.scheduleSave()
    this.render()
  }

  /** Prepend earlier turns (the viewer's "load earlier" and window widening). */
  loadOlder(): Promise<void> {
    if (this.olderRun) return this.olderRun
    const run = this.widen().finally(() => {
      this.olderRun = undefined
    })
    this.olderRun = run
    return run
  }

  /** Load until the window starts at or before `seq` (deep links to an event). */
  async focusSeq(seq: number): Promise<void> {
    await this.ready()
    while (!this.locked && this.windowStart > 1 && this.windowStart > seq) {
      const before = this.windowStart
      await this.loadOlder()
      if (this.windowStart === before) return
    }
  }

  private async widen() {
    const m = this.material
    const read = this.deps.readPage
    if (!m || !read || this.windowStart <= 1) return
    this.olderState = { loading: true, error: false }
    this.publishNow()
    try {
      let target = previousCut(m, this.windowStart, this.cut?.[1] ?? 1, OLDER_TURNS)
      for (;;) {
        const start = target ? target[0] : 1
        const older: TranscriptEvent[] = []
        let after = start - 1
        while (after < this.windowStart - 1) {
          const page = await read(after)
          if (this.locked) return
          for (const e of page.events) {
            if (e.seq >= this.windowStart) break
            if (e.seq !== after + 1) throw new Error('transcript sequence gap')
            older.push(e)
            after = e.seq
          }
          if (!page.more || !page.events.length) break
        }
        if (after !== this.windowStart - 1) throw new Error('older turns unavailable')
        const events = [...older, ...this.events]
        if (target && !windowSafe(events, m, target)) {
          // Still references an earlier turn: step back one more cut (ultimately the start).
          this.events = events
          this.windowStart = start
          target = previousCut(m, start, target[1], 1)
          continue
        }
        this.events = events
        this.windowStart = start
        this.cut = target
        const base = target ? target[1] - 1 : 0
        let w = buildWindow(events, base, this.runId)
        if (this.win.connection !== 'live')
          w = reduceTranscript(w, { kind: 'client.connection', state: this.win.connection })
        this.win = w
        break
      }
      this.olderState = { loading: false, error: false }
    } catch {
      this.olderState = { loading: false, error: true }
    }
    this.publishNow()
  }

  lock() {
    this.locked = true
    this.replaying = false
    this.caughtUp = false
    this.material = null
    this.events = []
    clearTimeout(this.saveTimer)
    this.win = createTranscript()
    this.set(this.win)
  }

  setConnection(state: ConnState) {
    if (this.locked) return
    this.win = reduceTranscript(this.win, {
      kind: 'client.connection',
      state: toViewerConnection(state),
    })
    this.publishNow()
  }

  private scheduleSave() {
    const cache = this.deps.cache
    const m = this.material
    if (!cache || !m || this.replaying || m.header.lastSeq <= this.savedSeq) return
    clearTimeout(this.saveTimer)
    this.saveTimer = setTimeout(() => {
      const cur = this.material
      if (this.locked || !cur || cur.header.lastSeq <= this.savedSeq) return
      this.savedSeq = cur.header.lastSeq
      void cache.save(cur).catch(() => {})
    }, CACHE_SAVE_MS)
  }

  private compose(): TranscriptView {
    const m = this.material
    if (!m) return this.win
    return composeWindow(this.win, m)
  }

  private render() {
    const v = this.compose()
    // Mid-replay pages are reduced at once but coalesced into one render while they keep coming
    // (a long history no longer re-renders the whole viewer per page). The last page, or a pause
    // longer than the window, publishes.
    if (this.replaying && this.now() - this.lastPublish < REPLAY_PUBLISH_MS) {
      this.view = v
      clearTimeout(this.replayTimer)
      this.replayTimer = setTimeout(() => this.set(this.view), REPLAY_PUBLISH_MS)
      return
    }
    this.set(v)
  }

  private publishNow() {
    if (this.locked) return
    this.set(this.compose())
  }

  private reconnectMarker(next: TranscriptEvent, resumedFrom: number): TranscriptEvent {
    // Sorts between the last rendered event and the first resumed one; never collides with a
    // daemon seq (it is not an integer) and never reaches the hub cursor.
    return {
      v: 0,
      runId: this.runId,
      seq: next.seq - 0.5,
      eventId: `client:reconnected:${this.reconnects}`,
      ts: next.ts,
      agent: next.agent,
      kind: 'marker',
      marker: 'reconnected',
      text: `Reconnected — resumed after event ${resumedFrom}`,
    } as TranscriptEvent
  }

  private now() {
    return this.deps.now?.() ?? Date.now()
  }

  private set(v: TranscriptView) {
    clearTimeout(this.replayTimer)
    this.replayTimer = undefined
    this.view = v
    if (v === this.published) return
    this.published = v
    this.lastPublish = this.now()
    for (const l of this.listeners) l()
  }
}

/** The newest cut that still shows `turns` turns; none = the run is short enough to load whole. */
export function pickWindowCut(m: RunMaterial, turns: number): WindowCut | undefined {
  const total = m.turnIds.length
  for (let i = m.cuts.length - 1; i >= 0; i--) {
    const c = m.cuts[i]
    if (!c || c[1] <= 1) return undefined
    if (total - c[1] + 1 >= turns) return c
  }
  return undefined
}

/** A cut before `start` at least `step` turns earlier than `turn` (none = from the start). */
function previousCut(
  m: RunMaterial,
  start: number,
  turn: number,
  step: number,
): WindowCut | undefined {
  for (let i = m.cuts.length - 1; i >= 0; i--) {
    const c = m.cuts[i]
    if (!c || c[1] <= 1) return undefined
    if (c[0] < start && c[1] <= turn - step) return c
  }
  return undefined
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | undefined> {
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(undefined), ms)
    p.then(
      (v) => {
        clearTimeout(t)
        resolve(v)
      },
      () => {
        clearTimeout(t)
        resolve(undefined)
      },
    )
  })
}
